/* eslint-disable camelcase */
import { Box, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { sprintf } from 'sprintf-js'
import { Port } from '../../types/models'
import { EmHanPort, EmHanConfig, PortConfigEmHan } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { Dropdown } from '../../components/atoms/Dropdown'
import { ListSelector } from '../../components/atoms/ListSelector'
import { ElectricMeter, Undo } from '@mui/icons-material'
import { HelpTitle } from '../../components/atoms/HelpTitle'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { renderNetMeterCountWarning } from './netMeterWarning'

/**
 * Controller for HAN power meter.
 */
export class HanPowerMeterPortControl extends PortControl<EmHanPort, EmHanConfig> {

  port: EmHanPort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as EmHanPort
  }

  getDescription(): string { return this.app.t('Energy consumption measurement using HAN interface') }

  getTypeName(): string { return this.app.t('PortType-Han Power Meter') }

  getName(): string {
    return this.app.t('PortName-Han Power Meter {number}').replace('{number}', `${this.number}`)
  }

  get group(): string { return this.app.t('PortGroup-Energy Metering') }

  get noDiff() { return true }

  getConfigDefaults(): PortConfigEmHan {
    return {
      driver: 'EM_HAN_DRIVER_HAN',
      enabled: false,
      to_summer_inverted: false,
      uart_baudrate: 'AUTO',
      uart_mode: '8N1',
      uart_instance: 1
    }
  }

  configTopLeft() : React.ReactNode | undefined {
    const onChange = (e: string) => {
      this.setConfig('uart_instance', parseInt(e))
    }
    const onChangeDriver = (driver: string) => {
      this.setConfig('driver', driver)
    }

    const driver: string = this.getConfig('driver')
    const toSummerInverted = this.getConfig('to_summer_inverted')

    return <Box>
      <Box>
        <Trans>Driver</Trans>
        <ListSelector
          items={{
            EM_HAN_DRIVER_HAN: this.app.t('HAN'),
            EM_HAN_DRIVER_RELAYED: this.app.t('Relayed Data'),
          }}
          icons={[ElectricMeter, Undo]}
          selected={driver || null}
          onSelect={onChangeDriver}
        />
      </Box>

      <HelpTitle title={this.app.t('Net Metering')} code="EM_HAN"/>
      <Box sx={{ display: 'flex' }}>
        <CustomSwitch data-cy="switch-To Summer" onClick={() => this.setConfig('to_summer_inverted', !toSummerInverted)} checked={!toSummerInverted}/>
        <Typography component="span" sx={{ ml: 2 }}>
          <Trans>Part of building net metering?</Trans>
        </Typography>
      </Box>

      {renderNetMeterCountWarning(this)}

      <Box sx={{ mt: 1, display: driver === 'EM_HAN_DRIVER_RELAYED' ? 'none' : 'block' }}>
        <Dropdown
          sx={{ mt: 2 }}
          label={this.app.t('Physical Port')}
          items={{
            0: '20', 1: '21', 2: '22', 3: '23'
          }}
          selected={this.getConfig('uart_instance')}
          onSelect={value => onChange(value)}
        />
      </Box>
    </Box>
  }

  configTopRight() : React.ReactNode | undefined {
    const { energy, power } = this.port.values

    return <Box>
      <Trans>Measuring Data</Trans>
      <Box sx={{ mb: 2 }}/>
      <Trans>Power</Trans>: {power === undefined ? <>—</> : `${sprintf('%f', power)} W`}<br/>
      <Trans>Energy</Trans>: {energy === undefined ? <>—</> : `${sprintf('%.3f', energy / 3.6e6)} kWh`}<br/>
    </Box>
  }
}
